import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import QuestionPicker, { Tier } from "../../components/QuestionPicker";
import { toast } from "sonner";
import {
  Loader2, Printer, FileText, X, Clock, Calculator, KeyRound, Link2, Check,
  ArrowDownNarrowWide, Undo2, Save, FilePlus2,
} from "lucide-react";

/**
 * Building a test to hand out on paper.
 *
 * The gap this fills: a tutor could choose questions and give out their
 * codes, and could not put a paper in front of a child on a desk.
 *
 * The paper itself is drawn by the question bank, not here, and that is
 * deliberate - the bank holds the renderer the questions were approved in,
 * and a second one in this portal would drift from it. This screen chooses
 * what goes on the paper and asks the bank for a signed link to it. The link
 * lasts a day, because a print link that works forever is one a child can be
 * handed, and the mark-scheme version is the same link with the answers on.
 */

/**
 * Easy first.
 *
 * Band before marks, because the band is a judgement about how hard the
 * question is and the marks are only how long it takes - a 1-mark gold
 * question belongs after a 4-mark bronze one. Questions the bank has not
 * banded go last rather than first: an unknown is not an easy one, and
 * opening a test with one would be the worst possible guess.
 *
 * Ties keep the order the tutor already had, because `sort` is stable and
 * because two equally hard questions are in a deliberate order often enough
 * to be worth preserving.
 */
const BAND_ORDER = { bronze: 0, silver: 1, gold: 2 };

function easyToHard(codes, meta) {
  const rank = (c) => {
    const m = meta[c] || {};
    const band = BAND_ORDER[m.difficulty];
    return [band === undefined ? 3 : band, m.marks ?? 99];
  };
  return [...codes].sort((a, b) => {
    const [ba, ma] = rank(a);
    const [bb, mb] = rank(b);
    return ba - bb || ma - mb;
  });
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

export default function AdminPrintTest() {
  const [sets, setSets] = useState([]);
  const [codes, setCodes] = useState([]);
  const [meta, setMeta] = useState({});
  const [title, setTitle] = useState("Monthly Test");
  const [subtitle, setSubtitle] = useState("");
  const [note, setNote] = useState("");
  const [minutes, setMinutes] = useState("");
  const [calculator, setCalculator] = useState("auto");
  const [busy, setBusy] = useState(null);
  const [links, setLinks] = useState({});
  // The order before a sort, so one click can undo it.
  const [previousOrder, setPreviousOrder] = useState(null);
  // The worksheet this was loaded from, so "save" can mean "update that one"
  // rather than quietly making a second copy of it every time.
  const [loadedSet, setLoadedSet] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/sets").then((r) => setSets(r.data || [])).catch(() => setSets([]));
  }, []);

  // Marks and topics for whatever is on the paper, so the total adds up on
  // screen before anybody prints it.
  useEffect(() => {
    if (codes.length === 0) { setMeta({}); return; }
    api.get("/admin/bank/question-meta", { params: { codes: codes.join(",") } })
      .then((r) => {
        const m = {};
        for (const q of r.data.questions || []) m[q.code] = q;
        setMeta(m);
      })
      .catch(() => setMeta({}));
  }, [codes]);

  const toggle = useCallback((c) => {
    setLinks({});
    setPreviousOrder(null);
    setCodes((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c]));
  }, []);

  // Has it drifted from the worksheet it was loaded from?
  const changedSinceLoad = useMemo(() => {
    if (!loadedSet) return false;
    const original = (sets.find((x) => x.id === loadedSet.id) || {}).question_codes || [];
    return original.length !== codes.length || original.some((c, i) => c !== codes[i]);
  }, [loadedSet, sets, codes]);

  const sortEasyToHard = () => {
    const sorted = easyToHard(codes, meta);
    if (sorted.every((c, i) => c === codes[i])) {
      toast.info("Already in order, easiest first");
      return;
    }
    setPreviousOrder(codes);
    setCodes(sorted);
    setLinks({});
    toast.success("Sorted easiest first");
  };

  const undoSort = () => {
    if (!previousOrder) return;
    setCodes(previousOrder);
    setPreviousOrder(null);
    setLinks({});
  };

  const loadSet = (id) => {
    const s = sets.find((x) => x.id === id);
    if (!s) return;
    setLinks({});
    setPreviousOrder(null);
    setCodes(s.question_codes || []);
    setTitle(s.title || "Monthly Test");
    setLoadedSet({ id: s.id, title: s.title });
    toast.success(`Loaded ${s.question_codes?.length || 0} questions from "${s.title}"`);
  };

  const refreshSets = () =>
    api.get("/admin/sets").then((r) => setSets(r.data || [])).catch(() => {});

  /**
   * Keep this paper.
   *
   * It is saved as a worksheet, which is the thing the rest of the portal
   * already understands: worksheets can be given out to a group, reissued
   * next year, and edited while the work already handed out stays frozen.
   * A second kind of saved paper would have been a second thing to keep in
   * step with all of that.
   */
  const saveAsNew = () => {
    // Named from the Title field rather than a pop-up asking again. The
    // tutor has already typed the name of the test at the top of the cover
    // sheet, and being asked for it a second time invites two names for one
    // thing.
    if (!title.trim()) { toast.error("Give the paper a title first"); return; }
    setSaving(true);
    api.post("/admin/sets", { title: title.trim(), question_codes: codes, kind: "test" })
      .then((r) => {
        setLoadedSet({ id: r.data.id, title: r.data.title });
        toast.success(`Saved as "${r.data.title}" — it is in Worksheets now`);
        return refreshSets();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save it"))
      .finally(() => setSaving(false));
  };

  const updateLoaded = () => {
    if (!loadedSet) return;
    setSaving(true);
    api.patch(`/admin/sets/${loadedSet.id}`, { question_codes: codes, title })
      .then((r) => {
        setLoadedSet({ id: loadedSet.id, title });
        toast.success(
          r.data.already_issued
            ? `Updated. Work already given out is unchanged (${r.data.already_issued} issue${r.data.already_issued === 1 ? "" : "s"}).`
            : "Worksheet updated",
        );
        return refreshSets();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not update it"))
      .finally(() => setSaving(false));
  };

  const totals = useMemo(() => {
    let marks = 0;
    let known = 0;
    const topics = new Set();
    const tiers = new Set();
    for (const c of codes) {
      const m = meta[c];
      if (m?.marks != null) { marks += m.marks; known += 1; }
      if (m?.topic) topics.add(m.topic);
      if (m?.tier) tiers.add(m.tier);
    }
    return { marks, known, topics: [...topics], tiers: [...tiers] };
  }, [codes, meta]);

  const make = (mode) => {
    if (codes.length === 0) { toast.error("Pick some questions first"); return; }
    setBusy(mode);
    api.post("/admin/bank/print-link", {
      question_codes: codes,
      title,
      subtitle,
      note,
      minutes: minutes ? Number(minutes) : null,
      calculator: calculator === "auto" ? null : calculator === "yes",
      mode,
    })
      .then((r) => {
        setLinks((l) => ({ ...l, [mode]: r.data.url }));
        window.open(`${r.data.url}&auto=1`, "_blank", "noopener");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not build the paper"))
      .finally(() => setBusy(null));
  };

  return (
    <AdminShell title="Print a test" subtitle="Build a paper and print it or save it as a PDF">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-5">
        <div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <Field label="Start from a worksheet" hint="Or just pick questions below.">
                  <select defaultValue="" onChange={(e) => e.target.value && loadSet(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Choose a worksheet…</option>
                    {sets.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title} · {(s.question_codes || []).length} questions
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {codes.length > 0 && (
                <button onClick={() => { setCodes([]); setLinks({}); }}
                  className="text-sm text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50">
                  Clear
                </button>
              )}
            </div>
          </div>

          {codes.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400">
                  On the paper, in this order ({codes.length})
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {totals.marks} marks
                    {totals.known < codes.length && ` · ${codes.length - totals.known} still loading`}
                  </span>
                  {previousOrder ? (
                    <button onClick={undoSort}
                      className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 hover:bg-slate-50">
                      <Undo2 size={11} /> Undo the sort
                    </button>
                  ) : (
                    <button onClick={sortEasyToHard} disabled={totals.known === 0}
                      title="Band first, then marks. Questions with no band go last."
                      className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 hover:bg-slate-50 disabled:opacity-40">
                      <ArrowDownNarrowWide size={11} /> Easy to hard
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {codes.map((c, i) => {
                  const band = meta[c]?.difficulty;
                  // Picked to be told apart on a dark chip. The first attempt
                  // used amber for bronze and a darker amber for gold, which
                  // are the same dot at 6px.
                  const dot = { bronze: "#ea8a3c", silver: "#cbd5e1", gold: "#fde047" }[band];
                  return (
                    <button key={c} onClick={() => toggle(c)}
                      title={`${band || "no band"} · remove from the paper`}
                      className="font-mono text-xs bg-slate-800 text-white rounded-full pl-2 pr-1.5 py-1 flex items-center gap-1.5">
                      <span className="text-slate-400">{i + 1}</span>
                      {dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />}
                      {c}
                      {meta[c]?.marks != null && <span className="text-slate-400">{meta[c].marks}m</span>}
                      <X size={11} />
                    </button>
                  );
                })}
              </div>
              {/* The tier of the paper as a whole, stated rather than implied.
                  A Foundation paper with one Higher question in it is the
                  mistake this is here to make visible. */}
              {totals.tiers.length > 0 && (
                <p className="text-[11px] mt-2 flex flex-wrap items-center gap-1.5">
                  {totals.tiers.length === 1 ? (
                    <>
                      <Tier value={totals.tiers[0]} />
                      <span className="text-slate-400">tier throughout</span>
                    </>
                  ) : (
                    <>
                      {totals.tiers.map((t) => <Tier key={t} value={t} />)}
                      <span className="text-amber-700 font-semibold">
                        mixed tiers &mdash; check this is what you meant
                      </span>
                    </>
                  )}
                </p>
              )}
              {totals.topics.length > 0 && (
                <p className="text-[11px] text-slate-400 mt-2">
                  Topics: {totals.topics.join(" · ")}
                </p>
              )}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">
              Add questions
            </h3>
            <QuestionPicker chosen={codes} onToggle={toggle} />
          </div>
        </div>

        <div className="lg:sticky lg:top-4 self-start space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400">The cover sheet</h3>
            <Field label="Title">
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="Subtitle" hint="Shown under the title. For example: Year 10 · October 2026">
              <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Year 10 · October 2026"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Minutes" hint="Blank works it out from the marks.">
                <input value={minutes} onChange={(e) => setMinutes(e.target.value.replace(/\\D/g, ""))}
                  inputMode="numeric" placeholder="auto"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </Field>
              <Field label="Calculator"
                hint="A mixed paper labels each question instead of the cover.">
                <select value={calculator} onChange={(e) => setCalculator(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white">
                  <option value="auto">From the questions</option>
                  <option value="no">Not allowed</option>
                  <option value="yes">Allowed</option>
                </select>
              </Field>
            </div>
            <Field label="A note to the student" hint="Optional. Printed on the cover sheet.">
              <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2}
                placeholder="Revise circle theorems before this one."
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400">Print</h3>
              <span className="text-sm font-bold text-slate-800">
                {codes.length} questions · {totals.marks} marks
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mb-3 flex items-start gap-1.5">
              <Clock size={12} className="mt-0.5 shrink-0" />
              {/* The icon and the text are flex items, so the sentence has to be
                  one child or every phrase becomes its own column. */}
              <span>
                The paper opens in a new tab with the print box already up. Choose{" "}
                <strong>Save as PDF</strong> as the printer to keep a copy, and turn{" "}
                <strong>Headers and footers</strong> off in that box &mdash; the paper prints its own,
                and the browser&rsquo;s would put the web address across the top of every sheet.
              </span>
            </p>

            <div className="flex gap-2 mb-3">
              <button onClick={saveAsNew} disabled={saving || codes.length === 0}
                title={`Keep these questions as a worksheet called "${title}"`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 border border-slate-300 text-xs font-semibold rounded-lg py-2 hover:bg-slate-50 disabled:opacity-40">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <FilePlus2 size={13} />}
                {loadedSet ? "Save as a copy" : "Save this paper"}
              </button>
              {loadedSet && (
                <button onClick={updateLoaded} disabled={saving || codes.length === 0 || !changedSinceLoad}
                  title={changedSinceLoad
                    ? `Update "${loadedSet.title}" with these questions`
                    : "Nothing has changed since you loaded it"}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 border border-slate-300 text-xs font-semibold rounded-lg py-2 hover:bg-slate-50 disabled:opacity-40">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {changedSinceLoad ? "Update it" : "Saved"}
                </button>
              )}
            </div>

            {loadedSet && (
              <p className="text-[11px] text-slate-400 mb-3">
                {changedSinceLoad
                  ? <>Changed since you loaded <strong>{loadedSet.title}</strong>.</>
                  : <>Saved as <strong>{loadedSet.title}</strong>. Give it out from Worksheets.</>}
              </p>
            )}

            <button onClick={() => make("paper")} disabled={busy !== null || codes.length === 0}
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-800 text-white text-sm font-semibold rounded-lg py-2.5 disabled:opacity-40">
              {busy === "paper" ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
              The student's paper
            </button>

            <button onClick={() => make("answers")} disabled={busy !== null || codes.length === 0}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 border border-slate-300 text-sm font-semibold rounded-lg py-2.5 hover:bg-slate-50 disabled:opacity-40">
              {busy === "answers" ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
              Your copy, with the mark scheme
            </button>

            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-3">
              The mark-scheme copy is yours. Both links stop working after a day,
              so one that gets forwarded is dead by the following week.
            </p>

            {Object.keys(links).length > 0 && (
              <div className="mt-3 space-y-1.5">
                {Object.entries(links).map(([mode, url]) => (
                  <button key={mode}
                    onClick={() => {
                      navigator.clipboard?.writeText(url)
                        .then(() => toast.success("Link copied"))
                        .catch(() => toast.error("Could not copy it"));
                    }}
                    className="w-full text-left text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5">
                    <Link2 size={11} />
                    copy the {mode === "paper" ? "paper" : "mark scheme"} link
                    <Check size={11} className="text-green-600" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2 inline-flex items-center gap-1.5">
              <FileText size={12} /> What prints
            </h3>
            <ul className="text-[11px] text-slate-500 space-y-1 list-disc pl-4">
              <li>A cover sheet: name, total marks, paper length, instructions, and the question grid.</li>
              <li>Every question in the order above, with room to write and its own mark total.</li>
              <li>The Genius Education watermark behind every page.</li>
              <li>
                <Calculator size={10} className="inline mb-0.5" /> A crossed-out calculator on the cover
                when it is a non-calculator paper.
              </li>
              <li>A footer on every sheet: page number and &ldquo;Turn over&rdquo;.</li>
              <li>Room to write that grows with the marks &mdash; noticeably more from 3 marks up.</li>
              <li>
                On a paper mixing calculator and non-calculator questions, a label on
                each question saying which &mdash; the cover cannot say it for the whole paper.
              </li>
              <li>No exam board and no paper reference anywhere on it.</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
